import { useMemo } from "react";

import { EvalSample } from "@tsmono/inspect-common/types";
import { useMapAsyncData } from "@tsmono/react/hooks";
import { AsyncData } from "@tsmono/util";

import { Events } from "../@types/extraInspect";
import { sampleIdsEqual } from "../app/shared/sample";
import { SampleHandle, SampleStatus } from "../app/types";
import { SampleSummary } from "../client/api/types";

import { type ChunkedSample } from "./chunked";
import { ChunkedSampleData, useChunkedSample } from "./chunkedSampleQuery";
import { RunningSampleData, useRunningSample } from "./runningSampleQuery";
import { usePassiveEvalSample, useSample } from "./sampleQuery";
import { useSampleSummaries } from "./sampleSummaries";

const kNoRunningEvents: Events = [];

export interface EvalSampleData {
  /** The settled EvalSample: completed fetch, error-summary fallback, or
   *  a just-finalized streaming sample. */
  sample: EvalSample | undefined;
  status: SampleStatus;
  error: Error | undefined;
  /** Streamed events for a still-running sample; empty on the completed path. */
  running: Events;
  /** True when the preprocessor stripped events from an oversized sample
   *  (messages remain). */
  eventsCleared: boolean;
  /** Streaming path only: the event backlog is still loading (history
   *  draining), as opposed to waiting on live output. Drives the
   *  "Loading events…" affordances. */
  backfilling: boolean;
  /** Set when a chunked-shape sample renders through the windowed path:
   *  the transcript reads windowed rows through this instead of
   *  `sample.events` (which the shell-synthesized `sample` carries empty).
   *  Absent on every other path — including chunked samples that were fully
   *  hydrated for timeline fidelity (their `sample` is complete). */
  chunked?: ChunkedSample;
}

const settledSampleData = (sample: EvalSample): EvalSampleData => ({
  sample,
  status: "ok",
  error: undefined,
  running: kNoRunningEvents,
  eventsCleared: sample.events.length === 0 && sample.messages.length > 0,
  backfilling: false,
});

export interface SampleDataInputs {
  handle: SampleHandle | undefined;
  /** The log's merged summaries query. */
  summaries: AsyncData<SampleSummary[]>;
  /** The sample's merged summary (undefined while the summaries settle or
   *  when the handle isn't among them). */
  summary: SampleSummary | undefined;
  /** Chunked-shape classification + open (null datum = monolith sample). */
  chunked: AsyncData<ChunkedSampleData | null>;
  /** The completed-path EvalSample query. */
  query: AsyncData<EvalSample>;
  /** The running-path stream query. */
  running: AsyncData<RunningSampleData>;
  /** The finalized EvalSample a running stream primed into the sample cache
   *  (a passive read: not-resident reads as loading). */
  finalizedSample: AsyncData<EvalSample>;
}

/** The path-selection state machine (exported for tests). */
export const deriveSampleData = ({
  handle,
  summaries,
  summary,
  chunked,
  query,
  running,
  finalizedSample,
}: SampleDataInputs): EvalSampleData => {
  if (handle === undefined) {
    return {
      sample: undefined,
      status: "ok",
      error: undefined,
      running: kNoRunningEvents,
      eventsCleared: false,
      backfilling: false,
    };
  }
  if (summaries.error) {
    return {
      sample: undefined,
      status: "error",
      error: summaries.error,
      running: kNoRunningEvents,
      eventsCleared: false,
      backfilling: false,
    };
  }
  // Without a settled summary the sample isn't loadable yet (the legacy
  // loader waited for it too): loading while the summaries settle, idle when
  // they settled without this handle.
  if (summary === undefined) {
    return {
      sample: undefined,
      status: summaries.loading ? "loading" : "ok",
      error: undefined,
      running: kNoRunningEvents,
      eventsCleared: false,
      backfilling: false,
    };
  }
  // `completed !== false` mirrors the legacy loader: only an explicitly
  // incomplete summary takes the running path.
  if (summary.completed === false) {
    if (
      running.data?.finalized === true &&
      finalizedSample.data !== undefined
    ) {
      return settledSampleData(finalizedSample.data);
    }
    if (running.error) {
      return {
        sample: undefined,
        status: "error",
        error: running.error,
        running: kNoRunningEvents,
        eventsCleared: false,
        backfilling: false,
      };
    }
    return {
      sample: undefined,
      status: running.data === undefined ? "loading" : "streaming",
      error: undefined,
      running: running.data?.events ?? kNoRunningEvents,
      eventsCleared: false,
      backfilling: running.data?.backfilling ?? false,
    };
  }
  // Chunked-shape samples resolve before the completed path (the completed
  // fetch is gated on this settling null, so the branches are exclusive).
  if (chunked.data) {
    return {
      sample: chunked.data.evalSample,
      status: "ok",
      error: undefined,
      running: kNoRunningEvents,
      eventsCleared: false,
      backfilling: false,
      chunked: chunked.data.chunked,
    };
  }
  if (chunked.error) {
    return {
      sample: undefined,
      status: "error",
      error: chunked.error,
      running: kNoRunningEvents,
      eventsCleared: false,
      backfilling: false,
    };
  }
  if (query.data !== undefined) {
    return settledSampleData(query.data);
  }
  // The summary settled before the stream finalized: keep showing the
  // stream's cached events while the completed fetch settles.
  if (
    query.loading &&
    running.data !== undefined &&
    running.data.events.length > 0
  ) {
    // Bridging cached stream events while the completed fetch settles: the
    // sample is done, so never present the bridge as backlog-loading.
    return {
      sample: undefined,
      status: "streaming",
      error: undefined,
      running: running.data.events,
      eventsCleared: false,
      backfilling: false,
    };
  }
  const loading = chunked.loading || query.loading;
  return {
    sample: undefined,
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    status: loading ? "loading" : query.error ? "error" : "ok",
    error: loading ? undefined : query.error,
    running: kNoRunningEvents,
    eventsCleared: false,
    backfilling: false,
  };
};

/**
 * A sample's data — EvalSample, stream, and status — as one derivation over
 * the subsystem's sample queries. Which path serves the EvalSample (completed
 * fetch, error-summary fallback, live stream, finalize handoff) is
 * subsystem-private; a finalizing stream primes the completed EvalSample into
 * the sample cache so the handoff never flashes a loading state, and if the
 * summary settles before the stream finalizes, the stream's cached events
 * bridge the completed fetch.
 */
export const useEvalSampleData = (
  logDir: string,
  handle: SampleHandle | undefined
): EvalSampleData => {
  const summaries = useSampleSummaries(logDir, handle?.logFile);
  const summary = useMemo(
    () =>
      handle === undefined
        ? undefined
        : summaries.data?.find(
            (s) => sampleIdsEqual(s.id, handle.id) && s.epoch === handle.epoch
          ),
    [summaries, handle]
  );
  const runningPath = summary?.completed === false;
  const chunked = useChunkedSample(
    !runningPath && summary !== undefined ? handle : undefined
  );
  // Gated on the chunked classification settling null (monolith) so exactly
  // one path acquires the sample; the classification itself costs no fetch.
  const query = useSample(
    !runningPath && summary !== undefined && chunked.data === null
      ? handle
      : undefined,
    summary
  );
  const running = useRunningSample(logDir, handle, summary);
  // The finalized EvalSample a running stream primed; read passively so the
  // completed-path fetch stays owned by useSample.
  const finalizedSample = usePassiveEvalSample(handle);

  return useMemo(
    () =>
      deriveSampleData({
        handle,
        summaries,
        summary,
        chunked,
        query,
        running,
        finalizedSample,
      }),
    [handle, summaries, summary, chunked, query, running, finalizedSample]
  );
};

/**
 * The SampleData for a handle iff its EvalSample is resident — a passive
 * read that never fetches (see `usePassiveEvalSample` for the
 * cache-rendezvous contract; not-resident reads as loading). The
 * authoritative status for a sample being *shown* is `useEvalSampleData`'s.
 * For surfaces that must stay fetch-free (e.g. the invalidation banner in
 * the title bar).
 */
export const usePassiveEvalSampleData = (
  handle: SampleHandle | undefined
): AsyncData<EvalSampleData> => {
  const sample = usePassiveEvalSample(handle);
  return useMapAsyncData(sample, settledSampleData);
};
